<template class="modal-header">
  <Modal
    :show="show"
    body-classes="tw-overflow-y-scroll tw-z-10 tw-pb-0 tw-touch-manipulation"
    footer-classes="mobile-filter-refresh-modal-footer tw-p-0 tw-touch-manipulation"
    modal-classes="mobile-filter-refresh _mfr_animation mobile-filter-refresh-modal tw-rounded-none tw-overflow-none tw-overflow-y-none tw-m-0 tw-min-h-[100%] tw-max-h-[100%] tw-touch-manipulation"
    width="100%"
    @close="handleClose"
  >
    <template #header>
      <div
        :style="{
          backgroundImage: `url(${cdnAsset(
            'images/bg/uh-home-hero-bg-tile.png'
          )})`,
        }"
        class="modal-header-inner tw-text tw-flex tw-h-[60px] tw-w-full tw-items-baseline tw-justify-between tw-bg-repeat tw-px-[24px] tw-py-4"
      >
        <div
          class="tw-font-inter tw-z-10 tw-flex tw-gap-2 tw-text-base tw-font-semibold tw-leading-6 tw-text-white"
        >
          <img
            :src="cdnAsset('svg/listings/sliders-light.svg')"
            alt="Mobile Filter Icon"
            class="tw-h-[24px] tw-w-[24px]"
          />
          <span>Filters</span>
        </div>
        <div class="tw-z-0">
          <span
            class="tw-text-[24px] tw-leading-none tw-text-white"
            @click.prevent="handleClose"
          >
            <i class="far fa-times hover:tw-cursor-pointer"></i>
          </span>
        </div>
      </div>
    </template>

    <template #body>
      <div class="modal-body-inner tw-flex tw-flex-col tw-gap-6 lg:tw-flex-row">
        <div v-if="enableStudentEnquiriesCta && hasEnquiredProperties">
          <h3 class="tw-m-0 tw-p-0 tw-text-base tw-font-semibold">Enquiries</h3>
          <p class="tw-m-0 tw-p-0 tw-text-[14px]">
            This will override all current filters
          </p>
        </div>

        <div class="tw-w-full tw-divide-y lg:tw-w-1/3 xl:tw-w-1/4">
          <StackedEnquiredPropertyFilter
            v-if="enableStudentEnquiriesCta && hasEnquiredProperties"
            :checked="enquiredProperties"
            mobile
            @change="setEnquiredProperties"
          />

          <StackedPropertyTypeFilter
            multiple
            :show-count="enableLocalFilterCounts"
            :active="propertyType"
            :property-types="propertyTypes"
            @change="setPropertyType"
          />

          <StackedPriceFilter
            :min-placeholder="minListPrice"
            :max-placeholder="maxListPrice"
            :selected-min="minPrice"
            :selected-max="maxPrice"
            :disabled="enquiredProperties && enableStudentEnquiriesCta"
            @change="setPropertyPrice"
          />

          <StackedRoomsFilter
            :selected-bedrooms="bedrooms"
            :selected-bathrooms="bathrooms"
            :use-exact-room-counts="useExactRoomCounts"
            :disabled="enquiredProperties && enableStudentEnquiriesCta"
            @set-current-bedrooms="setBedrooms"
            @set-current-bathrooms="setBathrooms"
          />

          <div class="tw-divide-y">
            <StackedLocationAreaFilter
              :areas="areasWithCounts"
              :show-count="enableLocalFilterCounts"
              :current-area="currentArea"
              :disabled="enquiredProperties && enableStudentEnquiriesCta"
              @set-area="setArea"
            />

            <StackedNearbyUniversityFilter
              :universities="universitiesWithCounts"
              :show-count="enableLocalFilterCounts"
              :current-university="currentUniversity"
              :disabled="enquiredProperties && enableStudentEnquiriesCta"
              @set-university="setUniversity"
            />
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div
        class="modal-footer-styling tw-inline-flex tw-items-center tw-justify-between tw-p-[24px]"
      >
        <span
          class="tw-cursor-pointer tw-text-[16px] tw-font-medium tw-leading-[24px]"
          @click="handleClear"
          >Clear all filters</span
        >
        <button
          type="button"
          data-testid="mobile-stacked-filters-submit"
          class="px-0 tw-ease tw-relative tw-right-auto tw-top-0 tw-flex tw-w-auto tw-items-center tw-justify-center tw-gap-1 tw-rounded-full tw-bg-[#F48831] tw-px-[20px] tw-py-[10px] tw-text-center tw-text-[16px] tw-font-semibold tw-leading-[24px] tw-text-white tw-transition-all tw-duration-300 hover:tw-cursor-pointer hover:tw-bg-refreshPrimaryBg-dark"
          @click="handleShowResults"
        >
          <span>{{ submitLabel }}</span>
        </button>
      </div>
    </template>
  </Modal>
</template>

<script>
import Modal from '@components/Shared/UhModal';
import { useSearchModeStore } from '@js/stores/search/searchModeStore';
import StackedPriceFilter from '@components/Property/MobileSearchFilters/StackedPriceFilter.vue';
import StackedRoomsFilter from '@components/Property/MobileSearchFilters/StackedRoomsFilter.vue';
import StackedPropertyTypeFilter from '@components/Property/MobileSearchFilters/StackedPropertyTypeFilter.vue';
import StackedNearbyUniversityFilter from '@components/Property/MobileSearchFilters/StackedNearbyUniversityFilter.vue';
import StackedLocationAreaFilter from '@components/Property/MobileSearchFilters/StackedLocationAreaFilter.vue';
import {
  applySpareRoomsExclusivity,
  normalizePropertyTypeValue,
} from '@components/Property/MobileSearchFilters/helpers.ts';
import { PROPERTY_TYPES } from '@components/Property/MobileSearchFilters/constants.ts';
import usePropertyFilters from '@js/composables/usePropertyFilters';
import { pluralise, getCurrentPath } from '@js/utilities';
import StackedEnquiredPropertyFilter from '@components/Property/MobileSearchFilters/StackedEnquiredPropertyFilter.vue';

const { filterResults, filterCount } = usePropertyFilters();

export default {
  name: 'MobileSearchStackedFiltersModal',
  components: {
    StackedPriceFilter,
    StackedRoomsFilter,
    StackedPropertyTypeFilter,
    StackedNearbyUniversityFilter,
    StackedLocationAreaFilter,
    StackedEnquiredPropertyFilter,
    Modal,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    areas: {
      type: Array,
      required: true,
      default: () => [],
    },
    currentArea: {
      type: Object,
      required: false,
      default: null,
    },
    universities: {
      type: Array,
      required: true,
    },
    currentUniversity: {
      type: Object,
      required: false,
      default: null,
    },
    currentBedrooms: {
      type: [Number, String],
      default: null,
    },
    currentBathrooms: {
      type: [Number, String],
      default: null,
    },
    currentType: {
      type: String,
      default: null,
    },
    currentEnquiredProperties: {
      type: Boolean,
      default: false,
    },
    enablePrivateHallsFilter: {
      type: Boolean,
      default: false,
    },
    enableSpareRoomFunctionality: {
      type: Boolean,
      default: false,
    },
    enableLocalFilterCounts: {
      type: Boolean,
      default: false,
    },
    enableStudentEnquiriesCta: {
      type: Boolean,
      default: false,
    },
    useExactRoomCounts: {
      type: Boolean,
      default: false,
    },
    hasEnquiredProperties: {
      type: Boolean,
      default: false,
    },
  },
  data: () => {
    return {
      minPrice: null,
      maxPrice: null,
      minListPrice: null,
      maxListPrice: null,
      propertyType: [],
      resultsData: [],
      city: null,
      bedrooms: null,
      bathrooms: null,
      enquiredProperties: false,
      hasAppliedFilters: false, // Track if user has applied any filters
      initialFilterState: null, // Track initial state to detect changes
    };
  },
  computed: {
    blendedSearch() {
      return useSearchModeStore().blendedSearch;
    },
    areasWithCounts() {
      return this.areas.map((area) => {
        return {
          ...area,
          count: this.getCountPreview({ area_id: area.id }),
        };
      });
    },

    showFilterCount() {
      if (!this.enableLocalFilterCounts) {
        return false;
      }

      return this.filteredPropertyCount > 0;
    },

    universitiesWithCounts() {
      return this.universities.map((uni) => {
        return {
          ...uni,
          count: this.getCountPreview({ universities: uni.id }),
        };
      });
    },

    propertyTypes() {
      return PROPERTY_TYPES.map((type) => {
        const count = this.getCountPreview({
          type: type.id,
        });
        let visible = type.visible;

        if (type.id === 'private-halls') {
          visible = this.enablePrivateHallsFilter;
        } else if (type.id === 'spare-rooms') {
          visible = this.enableSpareRoomFunctionality;
        }

        return {
          ...type,
          count,
          visible,
        };
      });
    },

    appliedFilters() {
      let filters = {};

      if (this.minPrice) {
        filters.price_min = this.minPrice;
      }

      if (this.maxPrice) {
        filters.price_max = this.maxPrice;
      }

      if (this.bedrooms) {
        filters.bedroom_count = this.bedrooms;
      }

      if (this.bathrooms) {
        filters.bathroom_count = this.bathrooms;
      }

      if (this.currentType) {
        filters.type = this.currentType;
      }

      if (this.currentUniversity) {
        filters.universities = this.currentUniversity.id;
      }

      if (this.currentArea) {
        filters.area_id = this.currentArea.id;
      }

      if (this.enquiredProperties) {
        filters.enquired_properties = this.enquiredProperties;
      }

      return filters;
    },

    filteredProperties() {
      const filters = this.appliedFilters;

      if (!this.enableLocalFilterCounts) {
        return [];
      }

      return filterResults(filters);
    },

    filteredPropertyCount() {
      if (!this.enableLocalFilterCounts) {
        return 0;
      }

      return this.filteredProperties.length ?? 0;
    },

    submitLabel() {
      const showFilterCount = this.showFilterCount;
      const filteredPropertyCount = this.filteredPropertyCount;
      const currentPath = this.getCurrentPath();
      const enquiredProperties = this.enquiredProperties;
      const enableStudentEnquiriesCta = this.enableStudentEnquiriesCta;
      const resultPluralised = pluralise(
        filteredPropertyCount,
        'result',
        'results'
      );

      if (
        !enquiredProperties &&
        enableStudentEnquiriesCta &&
        currentPath &&
        currentPath === '/student-accommodation'
      ) {
        return 'Resume search';
      }

      if (showFilterCount) {
        return `Show ${filteredPropertyCount} ${resultPluralised}`;
      }

      return `Show ${resultPluralised}`;
    },
  },
  watch: {
    show(isOpen) {
      if (isOpen) {
        this.syncPropertyTypeFromCurrentType();
      }
    },
    currentBedrooms(newVal) {
      this.bedrooms =
        newVal !== null && newVal !== undefined && newVal !== ''
          ? newVal
          : null;
    },
    currentBathrooms(newVal) {
      this.bathrooms =
        newVal !== null && newVal !== undefined && newVal !== ''
          ? newVal
          : null;
    },
  },
  mounted() {
    this.getCityFromUrl();
    this.getFiltersFromQuery();
    this.fetchResultsData();

    if (this.currentBedrooms) {
      this.bedrooms = this.currentBedrooms;
    }

    if (this.currentBathrooms) {
      this.bathrooms = this.currentBathrooms;
    }

    if (this.currentType) {
      this.syncPropertyTypeFromCurrentType();
    }

    if (this.currentEnquiredProperties) {
      this.enquiredProperties = this.currentEnquiredProperties;
    }

    // Store initial filter state to detect changes
    this.storeInitialFilterState();
  },
  methods: {
    pluralise,
    getCurrentPath,
    handleClear() {
      this.setPropertyPrice({ min: null, max: null });
      this.setPropertyType(null);
      this.setBedrooms(null);
      this.setBathrooms(null);
      this.setArea(null);
      this.setUniversity(null);
      // If the user is currently viewing Enquiries, clearing filters should
      // keep them in Enquiries (just remove any extra filters).
      this.setEnquiredProperties(!!this.currentEnquiredProperties);

      // Clear filter tracking state when user clears all filters
      this.clearFilterTrackingState();

      this.handleClose();
    },
    handleClose() {
      // Remove style added to body element to prevent double scroll
      document.body.classList.remove('tw-overflow-hidden');
      this.$emit('close');
    },
    getCountPreview(filters) {
      return filterCount({ ...this.appliedFilters, ...filters });
    },
    handleShowResults() {
      // Close the modal
      this.handleClose();
    },
    syncPropertyTypeFromCurrentType() {
      this.propertyType = normalizePropertyTypeValue(this.currentType);
    },
    setPropertyType(propertyType) {
      // Blended results list spare rooms alongside every other type, so the selection is kept
      // as the user made it and only reshaped into the array this modal works with.
      if (this.blendedSearch) {
        propertyType = propertyType
          ? normalizePropertyTypeValue(propertyType)
          : propertyType;
      } else if (propertyType && Array.isArray(propertyType)) {
        propertyType = applySpareRoomsExclusivity(propertyType);
      } else if (propertyType === 'spare-rooms') {
        // Single selection: spare-rooms selected
        propertyType = ['spare-rooms'];
      } else if (propertyType && propertyType !== 'spare-rooms') {
        // Single selection: any other type selected, ensure spare-rooms is removed
        if (
          Array.isArray(this.propertyType) &&
          this.propertyType.includes('spare-rooms')
        ) {
          propertyType = Array.isArray(propertyType)
            ? propertyType
            : [propertyType];
          propertyType = propertyType.filter((type) => type !== 'spare-rooms');
        }
      }

      this.propertyType = propertyType;
      this.checkForFilterChange();
      this.updatePricePlaceholders();
      this.$emit('set-property-type', propertyType);
    },
    setPropertyPrice({ min, max }) {
      this.minPrice = min;
      this.maxPrice = max;
      this.checkForFilterChange();
      this.$emit('set-min-price', min);
      this.$emit('set-max-price', max);
    },
    setBedrooms(val) {
      this.bedrooms = val;
      this.checkForFilterChange();
      this.updatePricePlaceholders();
      this.$emit('set-current-bedrooms', val);
    },
    setBathrooms(val) {
      this.bathrooms = val;
      this.checkForFilterChange();
      this.updatePricePlaceholders();
      this.$emit('set-current-bathrooms', val);
    },
    setUniversity(university) {
      this.checkForFilterChange();
      this.$emit('set-university', university);
    },
    setArea(area) {
      this.checkForFilterChange();
      this.$emit('set-area', area);
    },
    setEnquiredProperties(checked) {
      this.enquiredProperties = checked;
      this.checkForFilterChange();
      this.$emit('set-enquired-properties', checked);
    },
    getCityFromUrl() {
      const pathSegments = window.location.pathname
        .split('/')
        .filter((item) => item !== '');

      if (
        pathSegments.length >= 2 &&
        pathSegments[0] === 'student-accommodation'
      ) {
        this.city = pathSegments[1];
      } else {
        this.city = null;
      }
    },
    getFiltersFromQuery() {
      const params = new URLSearchParams(window.location.search);

      // Set the `minPrice` and `maxPrice` from search params.
      const min = params.get('min-price');
      const max = params.get('max-price');

      if (min !== null && !isNaN(Number(min))) {
        this.minPrice = Number(min);
      } else {
        this.minPrice = null;
      }

      if (max !== null && !isNaN(Number(max))) {
        this.maxPrice = Number(max);
      } else {
        this.maxPrice = null;
      }

      // Set the `propertyType` from search params.
      const typeParam = params.get('type');

      if (typeParam) {
        this.propertyType = normalizePropertyTypeValue(typeParam);
      }

      // Set the `bedrooms` and `bathrooms` from search params.
      const bedroomsParam = params.get('bedrooms');
      const bathroomsParam = params.get('bathrooms');

      if (bedroomsParam !== null) {
        this.bedrooms = Number(bedroomsParam);
      }

      if (bathroomsParam !== null) {
        this.bathrooms = Number(bathroomsParam);
      }

      // Set the enquired properties filter from search params.
      const enquiredPropertiesParam = params.get('enquired-properties');

      if (enquiredPropertiesParam !== null) {
        this.enquiredProperties = enquiredPropertiesParam === '1';
      }

      // Check if URL has no filters - if so, clear filter tracking state
      const hasUrlFilters =
        min || max || typeParam || bedroomsParam || bathroomsParam;
      if (!hasUrlFilters) {
        this.clearFilterTrackingState();
      }
    },
    fetchResultsData() {
      if (!this.city) {
        return;
      }

      this.resultsData = [];

      // axios.get(`/api/v1/properties/${this.city}/filter-counts`).then(response => {
      //   const data = response.data;

      //   if (Array.isArray(data) && data.length > 0) {
      //     this.resultsData = data;
      //     this.updatePricePlaceholders();
      //   }

      // }).catch(error => {});
    },
    calculatePriceRange(data = this.resultsData) {
      const minPrices = data.map((item) => parseFloat(item.price_min));
      const maxPrices = data.map((item) => parseFloat(item.price_max));

      this.minListPrice = Math.min(...minPrices);
      this.maxListPrice = Math.max(...maxPrices);
    },
    updatePricePlaceholders() {
      let filteredData = this.resultsData;

      // Filter by property type.
      if (this.propertyType && this.propertyType.length > 0) {
        filteredData = filteredData.filter((item) =>
          this.propertyType.includes(item.type)
        );
      }

      // Filter by bedrooms.
      if (this.bedrooms !== null) {
        filteredData = filteredData.filter(
          (item) => item.bedroom_count === this.bedrooms
        );
      }

      // Filter by bathrooms.
      if (this.bathrooms !== null) {
        filteredData = filteredData.filter(
          (item) => item.bathroom_count >= this.bathrooms
        );
      }

      if (filteredData.length === 0) {
        this.minListPrice = null;
        this.maxListPrice = null;

        return;
      }

      this.calculatePriceRange(filteredData);
    },
    storeInitialFilterState() {
      // Store the initial state of all filters to detect changes
      this.initialFilterState = {
        minPrice: this.minPrice,
        maxPrice: this.maxPrice,
        propertyType: Array.isArray(this.propertyType)
          ? [...this.propertyType]
          : this.propertyType,
        bedrooms: this.bedrooms,
        bathrooms: this.bathrooms,
        enquiredProperties: this.enquiredProperties,
        currentArea: this.currentArea?.slug || null,
        currentUniversity: this.currentUniversity?.id || null,
      };
    },
    checkForFilterChange() {
      if (!this.initialFilterState) return;

      // Check if any filter has changed from its initial state
      const currentState = {
        minPrice: this.minPrice,
        maxPrice: this.maxPrice,
        propertyType: Array.isArray(this.propertyType)
          ? [...this.propertyType]
          : this.propertyType,
        bedrooms: this.bedrooms,
        bathrooms: this.bathrooms,
        enquiredProperties: this.enquiredProperties,
        currentArea: this.currentArea?.slug || null,
        currentUniversity: this.currentUniversity?.id || null,
      };

      const hasChanged =
        currentState.minPrice !== this.initialFilterState.minPrice ||
        currentState.maxPrice !== this.initialFilterState.maxPrice ||
        currentState.bedrooms !== this.initialFilterState.bedrooms ||
        currentState.bathrooms !== this.initialFilterState.bathrooms ||
        currentState.currentArea !== this.initialFilterState.currentArea ||
        currentState.enquiredProperties !==
          this.initialFilterState.enquiredProperties ||
        currentState.currentUniversity !==
          this.initialFilterState.currentUniversity ||
        JSON.stringify(currentState.propertyType) !==
          JSON.stringify(this.initialFilterState.propertyType);

      if (hasChanged && !this.hasAppliedFilters) {
        this.hasAppliedFilters = true;
      }
    },
    shouldReportToSentry(error) {
      // Don't report user-initiated cancellations or expected errors
      if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
        return false;
      }

      // Don't report rate limiting (expected behavior)
      if (error.response?.status === 429) {
        return false;
      }

      // Don't report client-side network issues (user probably navigated away)
      if (error.code === 'ERR_NETWORK' && !error.response) {
        return false;
      }

      // Report server errors, auth failures, and validation errors
      if (error.response?.status >= 400) {
        return true;
      }

      // Report unexpected errors
      return true;
    },
    clearFilterTrackingState() {
      // Clear sessionStorage filter state
      if (typeof Storage !== 'undefined') {
        sessionStorage.removeItem('uh_filters_applied');
      }

      // Reset local tracking state
      this.hasAppliedFilters = false;
    },
  },
};
</script>

<style>
@media (max-width: 1023px) {
  /*
  * Ensure all styles are scoped to the mobile filter refresh modal
  * to prevent conflicts with other modals or components.
  */

  .mobile-filter-refresh-modal .modal-header {
    position: relative;
    padding: 0 !important;
  }

  .mobile-filter-refresh-modal .modal-header-inner {
    background-size: 548px;
  }

  .mobile-filter-refresh-modal .modal-header-inner::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(
      0deg,
      rgba(0, 162, 226, 0) -60%,
      #00a2e2 100%
    );
    background-repeat: no-repeat;
    background-size: contain;
    background-position: right;
    display: block;
  }
}

.mobile-filter-refresh-modal.mobile-filter-refresh .modal-body {
  height: 100% !important;
}

.mobile-filter-refresh-modal.mobile-filter-refresh
  .modal-body
  .modal-body-inner {
  padding: 8px 8px 0 8px;
}

.mobile-filter-refresh-modal-footer {
  border-top: 1px solid var(--Mono-Grey-300, #d1d5db) !important;
  background: var(--Mono-White, #fff) !important;
  box-shadow: 0 -4px 20px 0 rgba(0, 0, 0, 0.08) !important;
}
</style>

<!-- Unscoped styles for transition override -->
<style>
/* Keyframe animations for smoother mobile modal transitions */
@keyframes slideDownToBottom {
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(100vh);
  }
}

/* Override uni-pop transition for our specific modal */
._mfr_animation.mobile-filter-refresh.uni-pop-enter-active {
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
}

._mfr_animation.mobile-filter-refresh.uni-pop-leave-active {
  animation: slideDownToBottom 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
  /* Fallback transition for Firefox */
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
}

/* Firefox-specific fallback */
@-moz-document url-prefix() {
  ._mfr_animation.mobile-filter-refresh.uni-pop-leave-active {
    animation: none !important;
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
  }
}

._mfr_animation.mobile-filter-refresh.uni-pop-enter,
._mfr_animation.mobile-filter-refresh.uni-pop-leave-to {
  opacity: 0 !important;
  transform: translateY(100vh) !important;
}

._mfr_animation.mobile-filter-refresh.uni-pop-enter-to,
._mfr_animation.mobile-filter-refresh.uni-pop-leave-from {
  opacity: 1 !important;
  transform: translateY(0) !important;
}

._mfr_animation.mobile-filter-refresh.uni-pop-enter-to,
._mfr_animation.mobile-filter-refresh.uni-pop-leave-from {
  opacity: 1 !important;
  transform: translateY(0) !important;
}
</style>
